<template>
  <div
    class="rete-stage-node-frame"
    :style="{ width: data.width + 'px', height: data.height + 'px' }"
  >
    <div
      class="rete-stage-node"
      :class="{
        'rete-stage-node--selected': data.selected,
        'rete-stage-node--decision': data.flowIsDecision
      }"
      :style="{ '--stage-color': data.flowColor || '#462181' }"
      data-testid="rete-flow-node"
    >
      <div class="rete-stage-node__heading">
        <span class="rete-stage-node__badge">{{ data.flowOrder }}</span>
        <span class="rete-stage-node__title">{{ data.flowTitle }}</span>
        <button
          class="rete-stage-node__options"
          type="button"
          title="Abrir opções do estágio"
          :aria-label="`Abrir opções de ${data.flowTitle}`"
          :data-node-id="data.id"
          @pointerdown.stop
          @mousedown.stop
        >
          <i class="fas fa-ellipsis-v" aria-hidden="true"></i>
        </button>
      </div>
      <div
        class="rete-stage-node__port rete-stage-node__port--input"
        aria-label="Entrada"
      >
        <Ref
          :data="{
            type: 'socket',
            side: 'input',
            key: 'input',
            nodeId: data.id,
            payload: data.inputs.input.socket
          }"
          :emit="emit"
        />
      </div>
      <ReteInlineFields
        v-if="!data.flowIsDecision && data.flowInlineFields.length"
        :fields="data.flowInlineFields"
        @retry-data="emit({ type: 'retry-data', data: $event })"
      />
      <div
        v-if="!data.flowIsDecision && filePreviews.length"
        class="rete-stage-node__files"
      >
        <div
          v-for="file in filePreviews"
          :key="file.key"
          class="rete-stage-node__file"
        >
          <span class="rete-stage-node__file-label">{{ file.label }}</span>
          <button
            v-if="file.href"
            type="button"
            class="rete-stage-node__file-link"
            :class="{
              'rete-stage-node__file-link--image': file.isImage,
              'rete-stage-node__file-link--media': !file.isImage
            }"
            :title="file.name"
            :aria-label="`Visualizar ${file.name}`"
            @click.stop="abrirVisualizador(file)"
            @pointerdown.stop
            @mousedown.stop
          >
            <img
              v-if="file.isImage"
              class="rete-stage-node__file-image"
              :src="file.href"
              :alt="file.name"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
            />
            <template v-else>
              <i :class="['fas', file.icon]" aria-hidden="true"></i>
              <span>{{ file.name }}</span>
            </template>
          </button>
          <span v-else class="rete-stage-node__file-name">
            <i class="fas fa-file-alt" aria-hidden="true"></i>
            {{ file.name }}
          </span>
        </div>
      </div>
      <b-modal
        :id="`rete-file-preview-${_uid}`"
        v-model="visualizadorAberto"
        :title="
          arquivoVisualizado ? arquivoVisualizado.name : 'Prévia do arquivo'
        "
        size="xl"
        centered
        hide-footer
        body-class="rete-file-viewer__body"
        modal-class="rete-file-viewer"
        @hidden="arquivoVisualizado = null"
      >
        <img
          v-if="arquivoVisualizado && arquivoVisualizado.kind === 'image'"
          class="rete-file-viewer__image"
          :src="arquivoVisualizado.href"
          :alt="arquivoVisualizado.name"
        />
        <video
          v-else-if="arquivoVisualizado && arquivoVisualizado.kind === 'video'"
          class="rete-file-viewer__media"
          :src="arquivoVisualizado.href"
          controls
          playsinline
        ></video>
        <audio
          v-else-if="arquivoVisualizado && arquivoVisualizado.kind === 'audio'"
          class="rete-file-viewer__audio"
          :src="arquivoVisualizado.href"
          controls
        ></audio>
        <iframe
          v-else-if="arquivoVisualizado && arquivoVisualizado.kind === 'pdf'"
          class="rete-file-viewer__pdf"
          :src="arquivoVisualizado.href"
          :title="`Visualização de ${arquivoVisualizado.name}`"
        ></iframe>
        <div v-else class="rete-file-viewer__fallback">
          <i class="fas fa-file-alt" aria-hidden="true"></i>
          <span>Este formato não pode ser visualizado aqui.</span>
          <a
            v-if="arquivoVisualizado && arquivoVisualizado.href"
            :href="arquivoVisualizado.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir arquivo em outra aba
          </a>
        </div>
      </b-modal>
      <div
        v-if="data.flowIsDecision"
        class="rete-stage-node__branches"
        aria-label="Saídas por condição"
      >
        <div
          v-if="!data.flowBranches.length"
          class="rete-stage-node__empty-branches"
        >
          <i class="fas fa-project-diagram" aria-hidden="true"></i>
          <div>
            <strong>Nenhum estágio conectado</strong>
            <span>Adicione um estágio ligado a esta decisão.</span>
          </div>
          <button
            class="rete-stage-node__add-connected"
            type="button"
            :data-node-id="data.id"
            @pointerdown.stop
            @mousedown.stop
          >
            <i class="fas fa-plus" aria-hidden="true"></i>
            Adicionar estágio
          </button>
        </div>
        <div
          v-for="(branch, index) in data.flowBranches"
          :key="branch.key"
          class="rete-stage-node__branch"
        >
          <div class="rete-stage-node__branch-copy">
            <div class="rete-stage-node__condition-heading">
              <small>SE {{ index + 1 }}</small>
              <div
                v-if="branch.fields && branch.fields.advanced"
                class="rete-stage-node__condition-mode"
                @pointerdown.stop
                @mousedown.stop
              >
                <ReteBooleanField
                  v-model="branch.fields.advanced.value"
                  :field="branch.fields.advanced"
                  :compact="true"
                  :show-label="false"
                  @change="atualizarCondicao(branch)"
                />
              </div>
            </div>
            <ReteInlineFields
              v-if="branch.fields"
              class="rete-stage-node__condition-fields"
              :fields="branchInlineFields(branch)"
              @change="atualizarCondicao(branch)"
            />
            <span v-else>{{ branch.label }}</span>
          </div>
          <Ref
            :data="{
              type: 'socket',
              side: 'output',
              key: branch.key,
              nodeId: data.id,
              payload: data.outputs[branch.key].socket
            }"
            :emit="emit"
          />
        </div>
        <div
          class="rete-stage-node__branch rete-stage-node__branch--add"
          title="Arraste para criar uma nova condição"
          aria-label="Adicionar condição"
        >
          <Ref
            :data="{
              type: 'socket',
              side: 'output',
              key: data.flowAddOutput.key,
              nodeId: data.id,
              payload: data.outputs[data.flowAddOutput.key].socket
            }"
            :emit="emit"
          />
        </div>
      </div>
      <div
        v-else
        class="rete-stage-node__port rete-stage-node__port--output"
        aria-label="Próximo estágio"
      >
        <Ref
          :data="{
            type: 'socket',
            side: 'output',
            key: 'output',
            nodeId: data.id,
            payload: data.outputs.output.socket
          }"
          :emit="emit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Ref } from "rete-vue-plugin/vue2";
import ReteInlineFields from "./ReteInlineFields.vue";
import ReteBooleanField from "./ReteBooleanField.vue";

export default {
  components: { Ref, ReteInlineFields, ReteBooleanField },
  props: {
    data: { type: Object, required: true },
    emit: { type: Function, required: true }
  },
  data() {
    return {
      visualizadorAberto: false,
      arquivoVisualizado: null
    };
  },
  computed: {
    filePreviews() {
      const fields = this.data.flowStage?.ces_metadata?.campos;
      if (!Array.isArray(fields)) return [];
      return fields
        .flat()
        .filter((field) => field?.type === "file" && field.value)
        .map((field, index) => {
          const value = String(field.value).trim();
          const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(value);
          const href =
            /^https?:\/\//i.test(value) ||
            /^data:image\/(?:png|jpeg|gif|webp|bmp|svg\+xml);/i.test(value) ||
            (!hasScheme && value && !value.startsWith("//"))
              ? value
              : null;
          const name = value.split(/[/?#]/).filter(Boolean).pop() || value;
          const isImage = Boolean(
            href &&
              (/^data:image\//i.test(href) ||
                /\.(?:png|jpe?g|gif|webp|bmp|svg)(?:[?#]|$)/i.test(href))
          );
          const kind = isImage
            ? "image"
            : /\.(?:mp4|webm|ogv|mov|m4v)(?:[?#]|$)/i.test(value)
            ? "video"
            : /\.(?:mp3|wav|ogg|m4a|aac|flac|opus)(?:[?#]|$)/i.test(value)
            ? "audio"
            : /\.pdf(?:[?#]|$)/i.test(value)
            ? "pdf"
            : "file";
          return {
            key: `${field.bindText || "file"}-${index}`,
            label: field.label || "Arquivo",
            name,
            href,
            isImage,
            kind,
            icon:
              kind === "video"
                ? "fa-file-video"
                : kind === "audio"
                ? "fa-file-audio"
                : kind === "pdf"
                ? "fa-file-pdf"
                : "fa-file-alt"
          };
        });
    }
  },
  methods: {
    abrirVisualizador(file) {
      this.arquivoVisualizado = file;
      this.visualizadorAberto = true;
    },
    branchInlineFields(branch) {
      const fields = branch.fields;
      const advanced = fields.advanced?.value;
      const entries = advanced
        ? [{ field: fields.condition }]
        : [
            {
              field: fields.parameter,
              options: this.data.flowFieldOptions.cec_parametro || []
            },
            {
              field: fields.operator,
              options: this.data.flowFieldOptions.cec_operador || []
            },
            { field: fields.value }
          ];
      return entries.filter((entry) => entry.field);
    },
    atualizarCondicao(branch) {
      const fields = branch.fields;
      if (!fields.advanced?.value && fields.condition) {
        fields.condition.value = `{${fields.parameter?.value}} ${fields.operator?.value} ${fields.value?.value}`;
      }
    }
  }
};
</script>

<style lang="scss">
.rete-stage-node-frame {
  position: relative;
}

.rete-stage-node {
  background: #fff;
  border: 1px solid #e6e1ed;
  box-sizing: border-box;
  border-radius: 12px;
  box-shadow: 0 5px 16px rgba(39, 25, 61, 0.12);
  color: #30283c;
  font-family: Inter, Arial, sans-serif;
  min-width: 300px;
  overflow: visible;
  padding-bottom: 7px;
  position: relative;
  transform: scale(var(--rete-card-scale, 1));
  transform-origin: top left;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  width: 300px;
}
.rete-stage-node--selected {
  border-color: #8b68bd;
  box-shadow: 0 0 0 3px rgba(98, 58, 148, 0.18),
    0 8px 22px rgba(39, 25, 61, 0.16);
}
.rete-stage-node__files {
  display: grid;
  gap: 8px;
  padding: 0 11px 10px;
}
.rete-stage-node__file {
  display: grid;
  gap: 4px;
  min-width: 0;
}
.rete-stage-node__file-label {
  color: #766d82;
  font: 600 calc(10px * var(--rete-card-text-scale, 1)) Inter, Arial, sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rete-stage-node__file-link,
.rete-stage-node__file-name {
  align-items: center;
  background: #fbfaff;
  border: 1px solid #e7e1ee;
  border-radius: 7px;
  color: #60378e;
  display: flex;
  font: 600 calc(10px * var(--rete-card-text-scale, 1)) Inter, Arial, sans-serif;
  gap: 7px;
  min-height: 30px;
  min-width: 0;
  overflow: hidden;
  padding: 5px 7px;
  text-decoration: none;
}
.rete-stage-node__file-link {
  cursor: zoom-in;
  text-align: left;
  width: 100%;
}
.rete-stage-node__file-link:hover {
  border-color: #bba5d4;
  color: #462181;
  text-decoration: none;
}
.rete-stage-node__file-link > span,
.rete-stage-node__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rete-stage-node__file-image {
  background: #f1edf5;
  border-radius: 9px;
  display: block;
  height: 108px;
  object-fit: cover;
  transition: transform 0.22s ease;
  width: 100%;
}
.rete-stage-node__file-link--image {
  border-radius: 12px;
  height: 118px;
  display: block;
  overflow: hidden;
  padding: 4px;
}
.rete-stage-node__file-link--media {
  border-radius: 12px;
  flex-direction: column;
  height: 118px;
  justify-content: center;
  text-align: center;
}
.rete-stage-node__file-link--media > i {
  color: #8d70ad;
  font-size: 22px;
}
.rete-stage-node__file-link--media > span {
  max-width: 100%;
}
.rete-stage-node__file-link--image:hover .rete-stage-node__file-image {
  transform: scale(1.1);
}
.rete-file-viewer .modal-dialog {
  max-width: min(1120px, calc(100vw - 36px));
}
.rete-file-viewer .modal-content {
  background: #17141d;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  box-shadow: 0 24px 80px rgba(17, 12, 26, 0.4);
  color: #fff;
  overflow: hidden;
}
.rete-file-viewer .modal-header {
  align-items: center;
  background: #201c27;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 14px 20px;
}
.rete-file-viewer .modal-title {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rete-file-viewer .close {
  color: #fff;
  opacity: 0.75;
  text-shadow: none;
}
.rete-file-viewer__body {
  align-items: center;
  background: #121016;
  display: flex;
  justify-content: center;
  min-height: min(72vh, 760px);
  padding: 16px;
}
.rete-file-viewer__image {
  display: block;
  max-height: calc(82vh - 90px);
  max-width: 100%;
  object-fit: contain;
}
.rete-file-viewer__media {
  background: #000;
  max-height: calc(82vh - 90px);
  max-width: 100%;
  width: 100%;
}
.rete-file-viewer__audio {
  max-width: 720px;
  width: 100%;
}
.rete-file-viewer__pdf {
  background: #fff;
  border: 0;
  height: min(78vh, 820px);
  width: 100%;
}
.rete-file-viewer__fallback {
  align-items: center;
  color: #d5ccdf;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  min-height: 250px;
  text-align: center;
  > i {
    color: #b9a0d5;
    font-size: 42px;
  }
  > a {
    color: #d6b8ff;
    text-decoration: underline;
  }
}
.rete-stage-node {
  // AreaPlugin.resize escreve uma altura inline. O conteúdo define a altura
  // visual; o canvas mede esse tamanho para organizar e centralizar os cards.
  height: auto !important;
}
.rete-stage-node .rete-stage-node__condition-fields {
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: 5px;
  padding: 3px 0;
  .rete-inline-fields__field {
    font-size: calc(9px * var(--rete-card-text-scale, 1));
    gap: 3px;
    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .p-dropdown-label {
      font-size: calc(10px * var(--rete-card-text-scale, 1));
      padding: 6px 4px;
    }
    .p-dropdown-trigger {
      width: 18px;
      .pi {
        font-size: calc(9px * var(--rete-card-text-scale, 1));
      }
    }
    > input[type="text"] {
      font-size: calc(10px * var(--rete-card-text-scale, 1));
      padding: 6px 4px;
    }
  }
}
.rete-stage-node__heading {
  align-items: center;
  background: var(--stage-color, #462181);
  border-radius: 11px 11px 0 0;
  color: #fff;
  display: flex;
  gap: 9px;
  min-height: 45px;
  padding: 8px 11px;
}
.rete-stage-node__badge {
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 7px;
  display: inline-flex;
  flex: 0 0 25px;
  font-size: calc(11px * var(--rete-card-text-scale, 1));
  font-weight: 800;
  height: 25px;
  justify-content: center;
}
.rete-stage-node__title {
  flex: 1;
  font-size: calc(13px * var(--rete-card-text-scale, 1));
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.rete-stage-node__options {
  align-items: center;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(70, 33, 129, 0.18);
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(31, 20, 48, 0.2);
  color: #462181;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 26px;
  height: 28px;
  justify-content: center;
  margin: 0 -5px 0 0;
  padding: 0;
}
.rete-stage-node__options:hover,
.rete-stage-node__options:focus-visible {
  background: #fff;
  border-color: rgba(70, 33, 129, 0.42);
  box-shadow: 0 2px 7px rgba(31, 20, 48, 0.26);
  color: #351563;
  outline: none;
}
.rete-stage-node__options i {
  font-size: calc(13px * var(--rete-card-text-scale, 1));
}
.rete-stage-node__port {
  align-items: center;
  color: #746d80;
  display: flex;
  font-size: calc(10px * var(--rete-card-text-scale, 1));
  gap: 5px;
  height: 12px;
  min-height: 12px;
  overflow: visible;
}
.rete-stage-node__port--input {
  justify-content: flex-start;
  padding-left: 0;
}
.rete-stage-node__port--output {
  justify-content: flex-end;
  padding-right: 0;
}
.rete-stage-node__branches {
  padding: 2px 0 5px;
}
.rete-stage-node__branch {
  align-items: center;
  border-top: 1px solid #f0edf4;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-height: 38px;
  padding: 5px 0 5px 10px;
}
.rete-stage-node__branch-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.rete-stage-node__condition-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.rete-stage-node .rete-stage-node__condition-mode {
  margin-left: auto;
  .rete-boolean-field.rete-boolean-field--compact {
    background: transparent;
    border: 0;
    flex-wrap: nowrap;
    padding: 0;
    width: auto;
  }
  .rete-boolean-field__choices {
    padding: 2px;
  }
  .rete-boolean-field__choices button {
    font-size: calc(9px * var(--rete-card-text-scale, 1));
    padding: 2px 5px;
  }
}
.rete-stage-node__branch-copy small {
  color: #8d829a;
  font-size: calc(8px * var(--rete-card-text-scale, 1));
  font-weight: 800;
  letter-spacing: 0.06em;
}
.rete-stage-node__branch-copy > span {
  color: #4a4056;
  font-size: calc(10px * var(--rete-card-text-scale, 1));
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rete-stage-node__branch--add .rete-stage-node__branch-copy > span {
  color: #7047a4;
  font-weight: 700;
}
.rete-stage-node__branch--add {
  border-top: 1px dashed #d9e5f5;
  cursor: crosshair;
  justify-content: flex-end;
  min-height: 38px;
  padding: 5px 0 5px 10px;
}
.rete-flow-canvas .rete-stage-node .socket {
  background: var(--stage-color, #462181);
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px var(--stage-color, #462181);
  box-sizing: border-box;
  cursor: crosshair;
  height: 13px;
  margin: 0;
  position: relative;
  width: 13px;
}
.rete-flow-canvas .rete-stage-node .socket:hover {
  border-width: 2px;
}
.rete-flow-canvas .rete-stage-node__branch--add .socket {
  align-items: center;
  background: #3182f6;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #3182f6, 0 2px 6px rgba(49, 130, 246, 0.3);
  color: #fff;
  display: inline-flex;
  font-size: calc(10px * var(--rete-card-text-scale, 1));
  font-weight: 600;
  height: 13px;
  justify-content: center;
  line-height: 1;
  width: 13px;
}
.rete-stage-node__branch--add .socket::after {
  content: "+";
  transform: translateY(-0.5px);
}
.rete-stage-node__empty-branches {
  align-items: center;
  background: #faf8fd;
  border: 1px dashed #ded4eb;
  border-radius: 8px;
  color: #7047a4;
  display: grid;
  gap: 9px;
  grid-template-columns: auto minmax(0, 1fr);
  margin: 8px 11px;
  padding: 10px;
  > i {
    align-self: start;
    background: #f0e9f8;
    border-radius: 7px;
    padding: 8px;
  }
  > div {
    display: grid;
    gap: 3px;
    strong {
      color: #443653;
      font-size: calc(10px * var(--rete-card-text-scale, 1));
    }
    span {
      color: #81788f;
      font-size: calc(9px * var(--rete-card-text-scale, 1));
      font-weight: 400;
    }
  }
}
.rete-stage-node__add-connected {
  background: #7047a4;
  border: 0;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
  font: 600 calc(10px * var(--rete-card-text-scale, 1)) Inter, Arial, sans-serif;
  grid-column: 1 / -1;
  padding: 8px;
  &:hover,
  &:focus-visible {
    background: #60378e;
    outline: 2px solid rgba(112, 71, 164, 0.25);
    outline-offset: 2px;
  }
}
</style>
